import { type HTMLProps, type MouseEvent, type ReactNode, memo } from 'react';
import { css, type Theme } from '@emotion/react';
import { Plus } from '@phosphor-icons/react';
import { Button } from 'client/components/Button';
import { Empty } from 'client/components/Empty';
import { Typography } from 'client/components/Typography';
import { CatalogItem, type CatalogItemProps, CatalogItemSkeleton } from './CatalogItem';

export const CatalogSkeleton = memo<{ title?: boolean; numberOfItems?: number }>(({ title = false, numberOfItems = 1 }) => (
  <ul css={[catalogListStyle, title && skeletonTitleStyle]}>
    {new Array(numberOfItems).fill(null).map((_, index) => (
      <CatalogItemSkeleton key={index} />
    ))}
  </ul>
));

export interface CatalogProps extends Omit<HTMLProps<HTMLUListElement>, 'title'> {
  title?: string | JSX.Element | null;
  items: CatalogItemProps[];

  /** Empty state placeholder */
  empty?: CatalogItemProps | JSX.Element;

  /** Enable a "create new" button and add text to it */
  createButtonText?: string | JSX.Element | null;

  /** Add a custom "create new" button */
  createButton?: JSX.Element | null;

  /** Customs elements */
  children?: ReactNode;

  /** Create button onClick event (does not work with custom createButton) */
  onCreate?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const Catalog = ({
  title = null,
  items,
  empty = <Empty />,
  createButtonText = null,
  createButton = null,
  children,
  onCreate,
  ...other
}: CatalogProps) => {
  const isCreateButtonShown = !!createButtonText || !!createButton;

  return (
    <ul css={catalogListStyle} {...other}>
      <>
        {title && (
          <Typography variant="body2" css={titleStyle}>
            {title}
          </Typography>
        )}

        {!items.length && !children && empty}

        {items.map((item) => (
          <CatalogItem key={item.id} {...item} />
        ))}

        {children}

        {isCreateButtonShown && (
          <div css={actionsStyle}>
            {createButton || (
              <Button primary icon={<Plus weight="bold" />} onClick={onCreate}>
                {createButtonText}
              </Button>
            )}
          </div>
        )}
      </>
    </ul>
  );
};

const catalogListStyle = (theme: Theme) => css`
  margin: 0;
  padding: 0;
  max-width: 65ch;

  > li:first-of-type {
    margin-top: 0;
  }

  > .empty {
    padding: 1.5rem 0;
    border: 2px solid ${theme.color.grey[60]};
    border-radius: ${theme.borderRadius}px;
  }
`;

const titleStyle = css`
  &.body2 {
    padding: 0 0 0.35rem 0.9rem;
  }
`;

const actionsStyle = css`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const skeletonTitleStyle = css`
  margin-top: 25.6px;
`;
